import { Link } from '@tanstack/react-router';
import { useTranslation } from '@/context/TranslationContext';
import { useBlogPosts } from '@/lib/queries/blog';
import { SEO, generateBreadcrumbSchema } from '@/components/shared/seo';
import styles from './Blog.module.scss';

export function Blog() {
    const { language, t } = useTranslation();
    const { data: posts = [], isLoading: loading } = useBlogPosts();

    // Breadcrumb Schema für Blog
    const breadcrumbSchema = generateBreadcrumbSchema([
        { name: language === 'de' ? 'Startseite' : 'Home', url: '/' },
        { name: 'Blog', url: '/blog' },
    ]);

    const formatDate = (date: any) => {
        if (!date) return '';

        try {
            let d: Date;

            // Handle different date formats from Firebase
            if (date.toDate) {
                // Firebase Timestamp object
                d = date.toDate();
            } else if (date._seconds) {
                // Firebase Timestamp JSON format
                d = new Date(date._seconds * 1000);
            } else if (typeof date === 'string' || typeof date === 'number') {
                // String or number timestamp
                d = new Date(date);
            } else {
                // Already a Date object
                d = date;
            }

            // Validate the date is valid
            if (isNaN(d.getTime())) {
                return '';
            }

            return new Intl.DateTimeFormat('de-DE', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).format(d);
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    return (
        <div className={styles.blogContainer}>
            <SEO
                title={t('BLOG.TITLE')}
                description={t('BLOG.SUBTITLE')}
                keywords={language === 'de'
                    ? 'Blog, Webentwicklung, Technologie, Software, Tutorials, München'
                    : 'Blog, web development, technology, software, tutorials, Munich'}
                canonicalUrl="/blog"
                language={language}
                structuredData={breadcrumbSchema}
            />
            <header className={styles.blogHeader}>
                <h1 data-aos="fade-up">{t('BLOG.TITLE')}</h1>
                <p data-aos="fade-up" data-aos-delay="100">
                    {t('BLOG.SUBTITLE')}
                </p>
            </header>

            <div className={styles.blogContent}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner}></div>
                        <p>{t('BLOG.LOADING')}</p>
                    </div>
                ) : posts.length === 0 ? (
                    <div className={styles.noPosts}>
                        <p>{t('BLOG.NO_POSTS')}</p>
                    </div>
                ) : (
                    <div className={styles.blogGrid}>
                        {posts.map((post, index) => (
                            <article key={post.id} className={styles.blogCard} data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className={styles.blogCardImage}>
                                    <img src={post.imageUrl} alt={post.title} />
                                    <div className={styles.blogCardOverlay}></div>
                                </div>
                                <div className={styles.blogCardContent}>
                                    <div className={styles.blogCardMeta}>
                                        <span>
                                            <i className='bx bx-calendar'></i>
                                            {formatDate(post.date)}
                                        </span>
                                        <span>
                                            <i className='bx bx-user'></i>
                                            {post.author}
                                        </span>
                                    </div>
                                    <h3>{language === 'en' && post.titleEn ? post.titleEn : post.title}</h3>
                                    <p>{language === 'en' && post.excerptEn ? post.excerptEn : post.excerpt}</p>
                                    <div className={styles.tags}>
                                        {(language === 'en' && post.tagsEn ? post.tagsEn : post.tags).map((tag, i) => (
                                            <span key={i} className={styles.tag}>{tag}</span>
                                        ))}
                                    </div>
                                    <Link to="/blog/$slug" params={{ slug: post.slug } as any} className={styles.blogCardLink}>
                                        {t('BLOG.READ_MORE')}
                                        <i className='bx bx-right-arrow-alt'></i>
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
